import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Table,
  Typography,
  message,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useLoading } from "context/loadingContext";
import { bg_button, bg_shadow } from "constant/css";
import qpayPaymentList, { resolveQpayInvoice, qpayPaymentCheck } from "functions/admin/qpayPaymentList";
import { BUSINESS_TZ } from "functions/utils/ulaanbaatarTime";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;

const QPAY_TYPE_OPTIONS = [
  { value: "QPAY_PAYMENT", label: "PAYMENT (5570025382)" },
  { value: "QPAY_PURCHASE", label: "PURCHASE (5570137868)" },
];

function ubRangeToQpayStrings(range) {
  if (!range || !range[0] || !range[1]) return {};
  const start = range[0].tz(BUSINESS_TZ).startOf("day").format("YYYY-MM-DD HH:mm:ss");
  const end = range[1].tz(BUSINESS_TZ).endOf("day").format("YYYY-MM-DD HH:mm:ss");
  return { start_date: start, end_date: end };
}

function pickTableRows(data) {
  if (!data || typeof data !== "object") return [];
  const rows = /** @type {any} */ (data).rows;
  if (Array.isArray(rows) && rows.length > 0) {
    return rows.map((row, i) => ({ key: row.payment_id || row.id || i, ...row }));
  }
  return [];
}

const QpayPaymentListPage = () => {
  const [form] = Form.useForm();
  const offsetMode = Form.useWatch("offsetMode", form) || "none";
  const { showLoading, hideLoading } = useLoading();
  const [rawResponse, setRawResponse] = useState(null);
  const [tableRows, setTableRows] = useState([]);
  const [lookupRows, setLookupRows] = useState([]);

  const applyLookupRow = (row) => {
    const qpayType =
      row.qpayType === "QPAY_PURCHASE" ? "QPAY_PURCHASE" : "QPAY_PAYMENT";
    form.setFieldsValue({
      object_id: row.qpayId,
      qpayType,
    });
    message.info(`object_id болон credential тохирууллаа (${row.qpayType} → ${qpayType}).`);
  };

  const onLookupInvoice = async () => {
    const invoiceId = form.getFieldValue("smartpayInvoiceId");
    if (!invoiceId) {
      message.warning("SmartPay invoice дугаар оруулна уу.");
      return;
    }
    showLoading();
    try {
      const response = await resolveQpayInvoice(invoiceId);
      if (response?.success !== true) {
        message.error(response?.error?.message || "Олдсонгүй.");
        setLookupRows([]);
        return;
      }
      const rows = response.data?.rows || [];
      setLookupRows(rows);
      if (rows.length === 0) {
        message.warning("Энэ invoice дээр qpayHistory байхгүй.");
        return;
      }
      applyLookupRow(rows[0]);
    } finally {
      hideLoading();
    }
  };

  const columns = useMemo(() => {
    if (!tableRows.length) return [];
    const keys = Object.keys(tableRows[0]).filter((k) => k !== "key");
    return keys.slice(0, 12).map((k) => ({
      title: k,
      dataIndex: k,
      key: k,
      ellipsis: true,
      render: (v) => (v == null ? "—" : String(v)),
    }));
  }, [tableRows]);

  const onCheckFallback = async () => {
    const values = form.getFieldsValue();
    if (!values.object_id?.trim()) {
      message.warning("object_id оруулна уу.");
      return;
    }
    showLoading();
    try {
      const response = await qpayPaymentCheck({
        qpayType: values.qpayType,
        object_id: values.object_id.trim(),
      });
      if (response?.success !== true) {
        message.error(response?.error?.message || "payment/check амжилтгүй.");
        return;
      }
      setRawResponse(response.data);
      setTableRows(pickTableRows(response.data));
      message.success("payment/check хариу ирлээ (list биш).");
    } finally {
      hideLoading();
    }
  };

  const onSearch = async (values) => {
    showLoading();
    try {
      const body = {
        qpayType: values.qpayType,
        object_type: values.object_type || "INVOICE",
        object_id: values.object_id?.trim(),
        offsetMode: values.offsetMode || "none",
        page_number: values.page_number || 1,
        page_limit: values.page_limit || 50,
      };
      Object.assign(body, ubRangeToQpayStrings(values.dateRange));
      if (values.offsetMode === "merchant") {
        body.merchant_branch_code = values.merchant_branch_code?.trim();
        body.merchant_terminal_code = values.merchant_terminal_code?.trim();
        body.merchant_staff_code = values.merchant_staff_code?.trim();
      }
      const response = await qpayPaymentList(body);
      if (response?.success !== true) {
        const msg =
          response?.error?.message ||
          (typeof response?.error === "string" ? response.error : null) ||
          "QPay жагсаалт авахад алдаа гарлаа.";
        message.error(msg);
        return;
      }
      setRawResponse(response.data);
      setTableRows(pickTableRows(response.data));
      message.success("QPay хариу ирлээ.");
    } catch (e) {
      console.error(e);
      message.error("Хүсэлт амжилтгүй.");
    } finally {
      hideLoading();
    }
  };

  return (
    <div className="p-4 md:p-6 w-full max-w-6xl mx-auto">
      <h1 className="text-lg font-semibold mb-2">QPay — төлбөрийн жагсаалт</h1>
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="Зөвхөн унших support хэрэгсэл"
        description={
          <>
            QPay <Text code>payment/list</Text> API.{" "}
            <Text code>object_type=INVOICE</Text> үед ихэнх тохиолдолд{" "}
            <Text code>object_id</Text> нь QPay-ийн invoice_id (SmartPay{" "}
            <Text code>qpayHistory.qpayId</Text>) байна. Эхлээд дэээр{" "}
            <b>SmartPay invoice #</b> → «QPay ID авах», offset «Байхгүй», огноо
            хоосон үлдээж «Хайх» дарна уу.
          </>
        }
      />

      <Card className={`mb-4 ${bg_shadow}`}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            qpayType: "QPAY_PAYMENT",
            object_type: "INVOICE",
            offsetMode: "none",
            page_number: 1,
            page_limit: 50,
          }}
          onFinish={onSearch}
        >
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end mb-4">
            <Form.Item name="smartpayInvoiceId" label="SmartPay invoice # (qpayId олох)">
              <Input placeholder="Жишээ: 272041" />
            </Form.Item>
            <Button type="default" onClick={onLookupInvoice}>
              QPay ID авах
            </Button>
          </div>
          {lookupRows.length > 1 && (
            <Alert
              type="warning"
              showIcon
              className="mb-4"
              message="Олон qpayHistory байна — сүүлийн мөрийг автоматаар сонгосон. Доорх жагсаалтаас сонгоно уу."
            />
          )}
          {lookupRows.length > 0 && (
            <Table
              className="mb-4"
              size="small"
              pagination={false}
              dataSource={lookupRows.map((r) => ({ key: r.historyId, ...r }))}
              columns={[
                { title: "qpayId (object_id)", dataIndex: "qpayId", ellipsis: true },
                { title: "qpayType", dataIndex: "qpayType", width: 140 },
                { title: "invoice_code (list doc)", dataIndex: "merchantInvoiceCode", ellipsis: true },
                { title: "status", dataIndex: "status", width: 120 },
                {
                  title: "",
                  key: "pick",
                  width: 100,
                  render: (_, row) => (
                    <Button size="small" onClick={() => applyLookupRow(row)}>
                      qpayId
                    </Button>
                  ),
                },
                {
                  title: "",
                  key: "code",
                  width: 100,
                  render: (_, row) =>
                    row.merchantInvoiceCode ? (
                      <Button
                        size="small"
                        onClick={() => {
                          form.setFieldsValue({ object_id: row.merchantInvoiceCode });
                          message.info("object_id = merchant invoice_code (payment/list doc)");
                        }}
                      >
                        code
                      </Button>
                    ) : null,
                },
              ]}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
            <Form.Item
              name="qpayType"
              label="QPay credential"
              rules={[{ required: true, message: "Credential сонгоно уу" }]}
            >
              <Select options={QPAY_TYPE_OPTIONS} />
            </Form.Item>
            <Form.Item name="object_type" label="object_type" rules={[{ required: true }]}>
              <Input placeholder="INVOICE" />
            </Form.Item>
            <Form.Item
              name="object_id"
              label="object_id"
              rules={[{ required: true, message: "object_id оруулна уу" }]}
              className="md:col-span-2"
            >
              <Input placeholder="QPay invoice_id / barimt object_id" />
            </Form.Item>
            <Form.Item name="dateRange" label="Гүйлгээний огноо (заавал биш, УБ)" className="md:col-span-2">
              <DatePicker.RangePicker className="w-full" />
            </Form.Item>
            {offsetMode === "pagination" && (
              <>
                <Form.Item name="page_number" label="page_number">
                  <InputNumber min={1} className="w-full" />
                </Form.Item>
                <Form.Item name="page_limit" label="page_limit">
                  <InputNumber min={1} max={100} className="w-full" />
                </Form.Item>
              </>
            )}
          </div>

          <Collapse
            ghost
            className="mb-4"
            items={[
              {
                key: "merchantOffset",
                label: "Дэлгэрэнгүй offset",
                children: (
                  <>
                    <Form.Item name="offsetMode" label="Offset горим">
                      <Select
                        options={[
                          { value: "none", label: "Байхгүй (QPay doc — optional)" },
                          { value: "pagination", label: "Pagination (page_number / page_limit)" },
                          { value: "merchant", label: "Merchant branch / terminal / staff" },
                        ]}
                      />
                    </Form.Item>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Form.Item name="merchant_branch_code" label="merchant_branch_code">
                        <Input />
                      </Form.Item>
                      <Form.Item name="merchant_terminal_code" label="merchant_terminal_code">
                        <Input />
                      </Form.Item>
                      <Form.Item name="merchant_staff_code" label="merchant_staff_code">
                        <Input />
                      </Form.Item>
                    </div>
                  </>
                ),
              },
            ]}
          />

          <div className="flex flex-wrap gap-2">
            <Button
              type="primary"
              htmlType="submit"
              icon={<SearchOutlined />}
              className={`${bg_button} border-none`}
            >
              payment/list
            </Button>
            <Button type="default" onClick={onCheckFallback}>
              payment/check (fallback)
            </Button>
          </div>
        </Form>
      </Card>

      {tableRows.length > 0 && (
        <Card title="rows" className={`mb-4 ${bg_shadow}`}>
          <Table columns={columns} dataSource={tableRows} pagination={false} scroll={{ x: true }} size="small" />
        </Card>
      )}

      {rawResponse != null && (
        <Card title="QPay хариу (JSON)" className={bg_shadow}>
          <pre className="text-xs overflow-auto max-h-96 bg-gray-50 p-3 rounded">
            {JSON.stringify(rawResponse, null, 2)}
          </pre>
        </Card>
      )}
    </div>
  );
};

export default QpayPaymentListPage;
